import type { FC } from "react";

import { useTimingStats } from "../../hooks/useTimingStats";
import { formatDurationMs } from "../../utils/time";
import { DataPanel } from "../common/DataPanel";
import { DataState } from "../common/DataState";
import { MetricCard } from "../common/MetricCard";
import { SectionHeader } from "../common/SectionHeader";
import { TimingsCharts } from "./TimingsCharts";
import { TimingsDurationByKindChart } from "./TimingsDurationByKindChart";
import { TimingsEntriesTable } from "./TimingsEntriesTable";
import { TimingsFiltersPanel } from "./TimingsFiltersPanel";
import { TimingsTimelineChart } from "./TimingsTimelineChart";

export const TimingsView: FC = () => {
  const timings = useTimingStats({ debounceMs: 250, initialPageSize: 50 });

  const { totals } = timings;

  return (
    <div className="space-y-4">
      <DataPanel
        title="Timings"
        subtitle="Explore latency across phases, tools, and runs."
        icon={
          <svg
            className="h-5 w-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        }
        iconColor="sky"
        className="hover:shadow-none"
      >
        <TimingsFiltersPanel
          filters={timings.filters}
          isLoading={timings.isLoading}
          onSetFilters={timings.setFilters}
          onSetDateRange={timings.setDateRange}
          onReset={timings.reset}
          onRefresh={timings.refresh}
        />
      </DataPanel>

      <DataState
        loading={timings.isLoading && !timings.summary}
        error={timings.error?.message ?? null}
        empty={
          !timings.isLoading &&
          Boolean(timings.summary) &&
          timings.totalRows === 0
        }
        emptyTitle="No timing data"
        emptyDescription="No timing records match the current filters."
        className="space-y-4"
      >
        <TimingSummary totals={totals} />

        <TimingsCharts entries={timings.entriesRows} />

        <div className="grid gap-3 lg:grid-cols-2">
          <TimingsDurationByKindChart entries={timings.entriesRows} />
          <TimingsTimelineChart entries={timings.entriesRows} />
        </div>

        <TimingsEntriesTable
          entries={timings.entriesRows}
          total={timings.totalRows}
          page={timings.page}
          pageSize={timings.pageSize}
          isLoading={timings.isLoading}
          errorMessage={timings.error?.message ?? null}
          onPageChange={timings.setPage}
        />
      </DataState>
    </div>
  );
};

interface TimingSummaryProps {
  totals: ReturnType<typeof useTimingStats>["totals"];
}

function TimingSummary({ totals }: TimingSummaryProps) {
  return (
    <div className="space-y-3">
      <SectionHeader title="Summary" />
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <MetricCard
          label="Entries"
          value={totals ? totals.count.toLocaleString() : "—"}
          variant="emerald"
          icon={
            <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 7V3m8 4V3m-9 8h10m-12 8h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
              />
            </svg>
          }
        />
        <MetricCard
          label="Total duration"
          value={formatDurationMs(totals?.totalDurationMs ?? null)}
          variant="violet"
          icon={
            <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          }
        />
        <MetricCard
          label="Avg duration"
          value={formatDurationMs(totals?.avgDurationMs ?? null)}
          variant="indigo"
          icon={
            <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 6h16M4 12h8m-8 6h16"
              />
            </svg>
          }
        />
        <MetricCard
          label="Max duration"
          value={formatDurationMs(totals?.maxDurationMs ?? null)}
          variant="amber"
          icon={
            <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 10V3L4 14h7v7l9-11h-7z"
              />
            </svg>
          }
        />
      </div>
    </div>
  );
}
